import { useAuth } from '@/core/contexts/AuthContext';
import { useIsAdhaJourney } from '@/core/hooks';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { MessageOkDialog } from '@/components/molecules/dialogs/MessageOkDialog';
import { LoginScreen } from '@/modules/auth/components/LoginScreen';
import { useLogin } from '@/modules/auth/hooks/useLogin';
import { useAdhaLogin } from '@/modules/auth/hooks/useAdhaLogin';
import { adhaUaePassService } from '@/modules/auth/services/adhaUaePassService';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

export default function LoginRoute() {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const authUser = useAppStore((state) => state.auth.user);
    const isAdhaJourney = useIsAdhaJourney();
    const hasCompleteSession = isAuthenticated && (Boolean(authUser) || isAdhaJourney);
    const { login, isLoading, error } = useLogin();
    const {
        loginWithAdha,
        isLoading: adhaLoading,
        error: adhaError,
        clearError: clearAdhaLoginError,
        applyInlineError,
    } = useAdhaLogin();
    const router = useRouter();
    const navigation = useNavigation();
    const params = useLocalSearchParams<{ from?: string }>();
    const hasNavigatedRef = useRef(false);
    const isFromRegistrationSuccess = params.from === 'registration-success';
    const [adhaErrorDialogVisible, setAdhaErrorDialogVisible] = useState(false);
    const [adhaErrorDialogMessage, setAdhaErrorDialogMessage] = useState('');

    // "Continue as Guest" — the guest home screen itself fetches the public
    // catalogue (useGuestPropertiesList), so this just navigates there.
    const handleGuestPress = () => {
        router.push('/guest-home');
    };

    const handleLogin = async (email: string, password: string) => {
        try {
            // Pass 'passcode-login' as redirectTo to indicate we should clear local data
            const redirectTo = params.from === 'passcode-login' ? 'passcode-login' : undefined;
            await login({ email, password }, redirectTo);
        } catch (err) {
            // Login failed - error is handled by useLogin hook
            logger.error('LoginRoute', 'Login failed', err);
        }
    };

    const handleForgotPassword = () => {
        router.push('/forgot-password');
    };

    // Native: UAE Pass redirect + ADHA login API errors → MessageOkDialog
    useEffect(() => {
        if (Platform.OS === 'web') {
            return;
        }
        const pending = adhaUaePassService.consumeNativePendingUi();
        if (!pending) {
            return;
        }
        if (pending.kind === 'dialog') {
            setAdhaErrorDialogMessage(pending.message);
            setAdhaErrorDialogVisible(true);
            return;
        }
        applyInlineError(pending.message);
    }, [applyInlineError]);

    // Handle back press: if coming from registration success, go back to welcome
    useEffect(() => {
        if (!isFromRegistrationSuccess) return;

        const unsubscribe = navigation.addListener('beforeRemove', (e) => {
            // Only handle back press (not programmatic navigation)
            // Check for both 'GO_BACK' and 'POP' action types
            const actionType = e.data.action.type;
            const isBackAction = actionType === 'GO_BACK' || actionType === 'POP';
            
            if (isBackAction) {
                e.preventDefault();
                logger.info('LoginRoute', 'Back pressed from registration success, navigating to welcome');
                router.replace('/welcome');
            }
        });

        return unsubscribe;
    }, [navigation, isFromRegistrationSuccess, router]);

    // ✅ Redirect if already authenticated (unless coming from passcode login)
    const allowLoginWhenAuthenticated = params.from === 'passcode-login';
    
    useEffect(() => {
        // Don't redirect if we just navigated after login
        if (hasNavigatedRef.current) {
            return;
        }
        
        if (!authLoading && hasCompleteSession && !allowLoginWhenAuthenticated) {
            // Use replace instead of dismissAll + replace to avoid navigation errors
            // replace() will handle the navigation stack properly
            try {
                if (router.canGoBack()) {
                    router.dismissAll();
                }
            } catch (error) {
                // Ignore dismissAll errors - replace will handle navigation
                logger.debug('LoginRoute', 'dismissAll failed, using replace only', error);
            }
            router.replace('/(auth)/(home)');
        }
    }, [authLoading, hasCompleteSession, allowLoginWhenAuthenticated, router]);

    // Don't render login form if already authenticated (unless coming from passcode login) or still loading auth
    if (authLoading || (hasCompleteSession && !allowLoginWhenAuthenticated)) {
        return null;
    }

    return (
        <>
            <LoginScreen
                onLogin={handleLogin}
                onForgotPassword={handleForgotPassword}
                onUAEPassPress={loginWithAdha}
                onGuestPress={handleGuestPress}
                loading={isLoading || adhaLoading}
                error={error || adhaError}
            />
            <MessageOkDialog
                visible={adhaErrorDialogVisible}
                message={adhaErrorDialogMessage}
                onOk={() => {
                    setAdhaErrorDialogVisible(false);
                    setAdhaErrorDialogMessage('');
                    clearAdhaLoginError();
                }}
            />
        </>
    );
}
